import flattenDeep from 'lodash-es/flattenDeep';
import omit from 'lodash-es/omit';
import union from 'lodash-es/union';
import moment from 'moment';
import { push, replace } from 'redux-first-history';
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { createAppSlice } from '.';
import {
  closeCurrentRentParcel as closeCurrentRentParcelApi,
  closeManyCurrentRentParcel as closeManyCurrentRentParcelApi,
  createMultiParcelRent as createMultiParcelRentApi,
  createParcel as createParcelApi,
  createParcelRent as createParcelRentApi,
  deleteClusterParcel as deleteClusterParcelApi,
  deleteMultiParcels as deleteMultiParcelsApi,
  deleteParcel as deleteParcelApi,
  editParcel as editParcelApi,
  getAnonymousParcelsByIds,
  getParcel,
  getParcelHistory,
  getParcels,
  importParcels as importParcelsApi,
  linkManyTagsToParcels as linkManyTagsToParcelsApi,
  linkTagToParcel as linkTagToParcelApi,
  patchParcel as patchParcelApi,
  postGfrParcels,
  unlinkManyTagsFromParcels as unlinkManyTagsFromParcelsApi,
  unlinkTagFromParcel as unlinkTagFromParcelApi,
  updateClusterParcel as updateClusterParcelApi,
} from '../api/api';
import { clusterParcel as clusterParcelSchema, parcel as parcelSchema } from '../api/schema';
import { PARCEL_ERROR_MESSAGES, ParcelCategory, ParcelStatus } from '../constants/parcelsConstants';
import { SNACK_CONSTANTS } from '../constants/snackConstants';
import { I18n, LocaleKey } from '../i18n';
import { normalize } from '../utils';
import { ItemWithPermissions } from '../utils/permissions';
import { KeysToCamelCase } from '../utils/types';
import { ClusterId } from './clusters';
import { CropId, deleteCrop } from './crops';
import { observationsArchived } from './observations/archived';
import { alerting, errorHasOccurred, snackAdded, success } from './snacks';
import { Tag, TagId } from './tags';
import { userLoggedOut } from './users';

export type ParcelId = number;

export type Parcel = ItemWithPermissions & {
  id: ParcelId;
  cluster_id: ClusterId;
  current_tenant_cluster_id: ClusterId;
  owner_cluster_id?: ClusterId;
  name: string;
  status: ParcelStatus;
  area: number;
  current_crop_id?: CropId;
  category?: ParcelCategory;
  tags?: Tag[];
  geometry_area?: unknown;
};

type ClusterParcelId = string;

export type ClusterParcel = {
  id: ClusterParcelId;
  parcel_id: ParcelId;
  cluster_id: ClusterId;
  from_date: string;
  to_date: string | null;
  isVirtual?: boolean;
};

type NormalizedClusterParcel = KeysToCamelCase<ClusterParcel>;

type NormalizedParcels = {
  entities: { parcel: Record<ParcelId, Parcel> };
  result: ParcelId[];
};

type ImportedParcels = {
  success: Parcel[];
  errors: { parcel: Parcel }[];
};

type State = {
  isFetching: boolean;
  areImportedParcelsFetching: boolean;
  clusterParcelsByParcelId: Record<ParcelId, NormalizedClusterParcel[]>;
  creationErrors: ParcelCreationError[];
  importedParcels: Partial<ImportedParcels>;
  parcelById: Record<ParcelId, Parcel>;
  parcelsIds: ParcelId[];
  lastFetchedParcel: Parcel | null;
  focusedParcel?: ParcelId;
};

const initialState: State = {
  isFetching: false,
  areImportedParcelsFetching: false,
  clusterParcelsByParcelId: {},
  creationErrors: [],
  importedParcels: {},
  parcelById: {},
  parcelsIds: [],
  lastFetchedParcel: null,
};

type AppDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

type ParcelApiError = { error_code?: number; reason?: string; intersected_parcels?: Parcel[] };

type ParcelCreationError = {
  error?: string;
  error_code?: number;
  parcel: Parcel;
};

// the geometry travels as a geojson string; every caller stringifies before dispatching
type ParcelCreationPayload = {
  geometry_area: string;
  cluster_id: ClusterId;
  category: ParcelCategory;
  name?: string;
  crop_id?: CropId;
  crop_from_date?: string;
  crop_to_date?: string;
  tag_ids?: TagId[];
  from_date?: string;
};

type RentPayload = { from_date?: string; to_date?: string; cluster_id?: ClusterId };

type ParcelClosingArgs = { closingDate?: string | null; isDesarchive?: boolean };

function parcelErrorMessage(error: ParcelApiError, defaultKey: LocaleKey) {
  const key = error?.error_code ? PARCEL_ERROR_MESSAGES[error.error_code] : undefined;
  return I18n.t(key ?? defaultKey);
}

function parcelError(error: ParcelApiError, defaultKey: LocaleKey, dispatch: AppDispatch) {
  dispatch(
    snackAdded({
      type: SNACK_CONSTANTS.TYPE.ERROR,
      duration: 5000,
      message: parcelErrorMessage(error, defaultKey),
      action: SNACK_CONSTANTS.ACTION.OK,
    }),
  );
}

// a rent conflict is surfaced by the caller's own modal, so it must reject with the API object
// intact: miniSerializeError would drop `intersected_parcels` from `action.error`, while a rejected
// value stays untouched in `action.payload`. Any other failure gets the usual error snack.
function rentError<R>(e: unknown, dispatch: AppDispatch, rejectWithValue: (value: ParcelApiError) => R): R {
  const error = e as ParcelApiError;
  if (error?.reason === 'intersectedParcelsFound') {
    return rejectWithValue(error);
  }
  return errorHasOccurred(e, dispatch);
}

function mergeParcels(state: State, payload: NormalizedParcels) {
  state.parcelById = { ...state.parcelById, ...payload.entities.parcel };
  state.parcelsIds = union(state.parcelsIds, payload.result);
}

function rentSnackKey(isRent: boolean, isMulti: boolean): LocaleKey {
  if (isRent) {
    return isMulti ? 'ParcelHistory.parcels_rent_create_success' : 'ParcelHistory.parcel_rent_create_success';
  }
  return isMulti ? 'ParcelHistory.parcels_rent_archive_success' : 'ParcelHistory.parcel_rent_archive_success';
}

// a rent change merges history entries and moves the tenant, so both have to be re-read
async function refreshParcelAndHistory(dispatch: AppDispatch, parcelId: ParcelId) {
  await Promise.all([
    dispatch(parcelsSlice.actions.fetchParcelHistory(parcelId)),
    dispatch(parcelsSlice.actions.fetchParcel(parcelId)),
  ]);
}

function applyEditedParcel(state: State, payload: NormalizedParcels) {
  const { entities, result } = payload;
  const id = result as unknown as ParcelId;
  const edited = entities.parcel[id];
  if (!edited) return;
  state.parcelById[id] = { ...state.parcelById[id], ...edited };
  if (state.lastFetchedParcel?.id === id) {
    state.lastFetchedParcel = { ...state.lastFetchedParcel, ...edited };
  }
}

function addTag(state: State, parcelId: ParcelId, tag: Tag) {
  const parcel = state.parcelById[parcelId];
  if (parcel) {
    parcel.tags = [...(parcel.tags || []), tag];
  }
  if (state.lastFetchedParcel?.id === parcelId) {
    state.lastFetchedParcel.tags = [...(state.lastFetchedParcel.tags || []), tag];
  }
}

function removeTag(state: State, parcelId: ParcelId, tagId: TagId) {
  const parcel = state.parcelById[parcelId];
  if (parcel) {
    parcel.tags = (parcel.tags || []).filter((t) => t.id !== tagId);
  }
  if (state.lastFetchedParcel?.id === parcelId) {
    state.lastFetchedParcel.tags = (state.lastFetchedParcel.tags || []).filter((t) => t.id !== tagId);
  }
}

const parcelsSlice = createAppSlice({
  name: 'parcels',
  initialState,
  reducers: (create) => ({
    fetchParcel: create.asyncThunk(
      async (id: ParcelId, { dispatch }) => {
        const result = await alerting(() => getParcel(id), { dispatch });
        return normalize([result], [parcelSchema]) as NormalizedParcels;
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          mergeParcels(state, payload);
          state.lastFetchedParcel = Object.values(payload.entities.parcel)[0] ?? state.lastFetchedParcel;
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    fetchParcels: create.asyncThunk(
      async (_: void, { dispatch }) => {
        const result = await alerting(() => getParcels(), { dispatch });
        return normalize(result || {}, [parcelSchema]) as NormalizedParcels;
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          mergeParcels(state, payload);
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    fetchAnonymousParcelsByIds: create.asyncThunk(
      async (parcelIds: ParcelId[], { dispatch, signal }) => {
        const result = await alerting(() => getAnonymousParcelsByIds(parcelIds, signal), { dispatch });
        return normalize(result || {}, [parcelSchema]) as NormalizedParcels;
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          mergeParcels(state, payload);
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    focusParcel: create.reducer<ParcelId | undefined>((state, { payload }) => {
      state.focusedParcel = payload;
    }),
    clearParcelCreationErrors: create.reducer((state) => {
      state.creationErrors = [];
    }),
    parcelsCreated: create.reducer<NormalizedParcels>((state, { payload }) => {
      mergeParcels(state, payload);
    }),
    // RTK serializes a thunk's thrown value, so the errors are stored before rejecting
    parcelCreationErrorsReceived: create.reducer<ParcelCreationError[]>((state, { payload }) => {
      state.creationErrors = payload;
    }),
    /** Patch parcel properties */
    editParcel: create.asyncThunk(
      async ({ id, payload }: { id: ParcelId; payload: Partial<Parcel> }, { dispatch }) => {
        const result = await alerting(() => editParcelApi(id, payload), { dispatch });
        success('ParcelForm.parcel_edit_success', { dispatch });
        const { result: editedId } = normalize(result, parcelSchema) as NormalizedParcels;
        // the edit response is partial, so the parcel is re-read before it lands in the store
        const reread = await getParcel(editedId);
        return normalize(reread, parcelSchema) as NormalizedParcels;
      },
      {
        fulfilled: (state, { payload }) => {
          applyEditedParcel(state, payload);
        },
      },
    ),
    /** Patch parcel geometry with specific error codes */
    patchParcel: create.asyncThunk(
      async ({ id, payload }: { id: ParcelId; payload: Pick<Parcel, 'geometry_area'> }, { dispatch }) => {
        try {
          const result = await patchParcelApi(id, payload);
          return normalize(result, parcelSchema) as NormalizedParcels;
        } catch (e) {
          parcelError(e as ParcelApiError, 'ParcelForm.parcel_create_error', dispatch);
          throw e;
        }
      },
      {
        fulfilled: (state, { payload }) => {
          applyEditedParcel(state, payload);
        },
      },
    ),
    deleteParcel: create.asyncThunk(
      async ({ id, redirect }: { id: ParcelId; redirect?: boolean }, { dispatch }) => {
        await alerting(() => deleteParcelApi(id), { dispatch });
        if (redirect) {
          dispatch(replace('/parcels'));
        }
        success('ParcelForm.parcel_delete_success', { dispatch });
        return id;
      },
      {
        fulfilled: (state, { payload }) => {
          state.parcelsIds = state.parcelsIds.filter((id) => id !== payload);
          state.parcelById = omit(state.parcelById, payload);
          if (state.lastFetchedParcel?.id === payload) {
            state.lastFetchedParcel = null;
          }
        },
      },
    ),
    deleteMultiParcels: create.asyncThunk(
      async (ids: ParcelId[], { dispatch }) => {
        await alerting(() => deleteMultiParcelsApi(ids), { dispatch });
        success(ids.length > 1 ? 'DeleteParcelForm.parcels_delete_success' : 'ParcelForm.parcel_delete_success', {
          dispatch,
        });
        return ids;
      },
      {
        fulfilled: (state, { payload }) => {
          state.parcelsIds = state.parcelsIds.filter((id) => !payload.includes(id));
          state.parcelById = omit(state.parcelById, payload);
          if (state.lastFetchedParcel && payload.includes(state.lastFetchedParcel.id)) {
            state.lastFetchedParcel = null;
          }
        },
      },
    ),
    importParcels: create.asyncThunk(
      async (
        {
          clusterId,
          file,
          format,
          projectionName,
          projectionID,
          fromDate,
          type,
        }: {
          clusterId: ClusterId;
          file: File;
          format: string;
          projectionName?: string;
          projectionID?: string;
          fromDate?: string;
          type?: string;
        },
        { dispatch },
      ) => {
        const imported = (await alerting(
          () => importParcelsApi(clusterId, file, format, projectionName, projectionID, fromDate, type),
          { dispatch },
        )) as ImportedParcels;
        // the API sends the geometry as a JSON string on both branches
        imported.success.forEach((p) => {
          p.geometry_area = JSON.parse(p.geometry_area as string);
        });
        imported.errors = imported.errors || [];
        imported.errors.forEach(({ parcel }) => {
          if (parcel.geometry_area) {
            parcel.geometry_area = JSON.parse(parcel.geometry_area as string);
          }
        });
        return imported;
      },
      {
        pending: (state) => {
          state.areImportedParcelsFetching = true;
        },
        fulfilled: (state, { payload }) => {
          payload.errors.forEach(({ parcel }, index) => {
            parcel.id = -(index + 1);
          });
          for (const p of payload.success) {
            state.parcelById[p.id] = p;
            state.parcelsIds.push(p.id);
          }
          state.importedParcels = payload;
        },
        settled: (state) => {
          state.areImportedParcelsFetching = false;
        },
      },
    ),
    reinitImportedParcels: create.reducer((state) => {
      state.areImportedParcelsFetching = false;
      state.importedParcels = {};
    }),
    createGfrParcels: create.asyncThunk(
      async (
        {
          clusterId,
          gfrParcelIds,
          gfrParcelNames,
          gfrParcelCrops,
          gfrParcelTags,
          fromDate,
          withRedirect,
        }: {
          clusterId: ClusterId;
          gfrParcelIds: ParcelId[];
          gfrParcelNames?: Record<ParcelId, string>;
          gfrParcelCrops?: Record<ParcelId, { crop_id: CropId }>;
          gfrParcelTags?: Record<ParcelId, TagId[]>;
          fromDate?: string;
          withRedirect?: boolean;
        },
        { dispatch },
      ) => {
        const result = (await alerting(
          () => postGfrParcels(gfrParcelIds, gfrParcelNames, clusterId, fromDate, gfrParcelCrops, gfrParcelTags),
          { dispatch },
        )) as Parcel[];
        success('ParcelForm.gfr_parcels_create_success', { dispatch, duration: 5000 });
        if (withRedirect) {
          dispatch(push(result[0] ? `/parcels/${result[0].id}` : '/parcels'));
        }
        return { normalizedResult: normalize(result, [parcelSchema]) as NormalizedParcels, gfrParcelIds };
      },
      {
        fulfilled: (state, { payload }) => {
          // the allocated gfr parcels are replaced by the real ones, so drop them first
          state.parcelsIds = state.parcelsIds.filter((id) => !payload.gfrParcelIds.includes(id));
          state.parcelById = omit(state.parcelById, payload.gfrParcelIds);
          mergeParcels(state, payload.normalizedResult);
        },
      },
    ),
    fetchParcelHistory: create.asyncThunk(
      async (id: ParcelId, { dispatch }) => {
        const response = await alerting(() => getParcelHistory(id), { dispatch });
        // virtual history entries come back without an id, which normalizr needs
        const withIds = flattenDeep(response as ClusterParcel[]).map((h, i) => {
          if (!h.id) {
            return { ...h, id: `virtual-${Date.now()}-${i}`, isVirtual: true };
          }
          return h;
        });
        return {
          parcelId: id,
          data: normalize(withIds, [clusterParcelSchema]) as {
            entities: { clusterParcel?: Record<ClusterParcelId, NormalizedClusterParcel> };
            result: ClusterParcelId[];
          },
        };
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          const byId = payload.data.entities.clusterParcel || {};
          state.clusterParcelsByParcelId[payload.parcelId] = (payload.data.result || []).map((id) => byId[id]!);
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    createMultiParcelRent: create.asyncThunk(
      async ({ ids, payload }: { ids: ParcelId[]; payload: RentPayload }, { dispatch, rejectWithValue }) => {
        try {
          await createMultiParcelRentApi(ids, payload);
        } catch (e) {
          return rentError(e, dispatch, rejectWithValue);
        }
        success(rentSnackKey(!!payload.cluster_id, ids.length > 1), { dispatch, duration: 5000 });
        // no cluster means an archive
        if (!payload.cluster_id) {
          observationsArchived({ parcelIds: ids }, { dispatch });
        }
        // the tenant field can change on any of them, so the whole list is re-read
        await dispatch(parcelsSlice.actions.fetchParcels());
        return;
      },
    ),
    createParcelRent: create.asyncThunk(
      async ({ id, payload }: { id: ParcelId; payload: RentPayload }, { dispatch, rejectWithValue }) => {
        try {
          await createParcelRentApi(id, payload);
        } catch (e) {
          return rentError(e, dispatch, rejectWithValue);
        }
        success(rentSnackKey(!!payload.cluster_id, false), { dispatch, duration: 5000 });
        // no cluster means an archive
        if (!payload.cluster_id) {
          observationsArchived({ parcelIds: [id] }, { dispatch });
        }
        return refreshParcelAndHistory(dispatch, id);
      },
    ),
    updateClusterParcel: create.asyncThunk(
      async ({ id, payload }: { id: ClusterParcelId; payload: RentPayload }, { dispatch, rejectWithValue }) => {
        let response;
        try {
          response = (await updateClusterParcelApi(id, payload)) as { parcel_id: ParcelId };
        } catch (e) {
          return rentError(e, dispatch, rejectWithValue);
        }
        success('ParcelHistory.parcel_rent_update_success', { dispatch, duration: 5000 });
        return refreshParcelAndHistory(dispatch, response.parcel_id);
      },
    ),
    deleteClusterParcel: create.asyncThunk(
      async ({ id, parcelId }: { id: ClusterParcelId; parcelId: ParcelId }, { dispatch, rejectWithValue }) => {
        try {
          await deleteClusterParcelApi(id);
        } catch (e) {
          return rentError(e, dispatch, rejectWithValue);
        }
        success('ParcelHistory.parcel_rent_delete_success', { dispatch, duration: 5000 });
        return refreshParcelAndHistory(dispatch, parcelId);
      },
    ),
    closeCurrentRentParcel: create.asyncThunk(
      async (
        { parcelId, closingDate = null, isDesarchive = false }: ParcelClosingArgs & { parcelId: ParcelId },
        { dispatch, rejectWithValue },
      ) => {
        try {
          await closeCurrentRentParcelApi(parcelId, closingDate);
        } catch (e) {
          return rentError(e, dispatch, rejectWithValue);
        }
        success(
          isDesarchive ? 'ParcelHistory.active_parcel_success' : 'ParcelHistory.close_current_parcel_rent_success',
          { dispatch, duration: 5000 },
        );
        return refreshParcelAndHistory(dispatch, parcelId);
      },
    ),
    closeManyCurrentRentParcel: create.asyncThunk(
      async (
        { parcelIds, closingDate = null, isDesarchive = false }: ParcelClosingArgs & { parcelIds: ParcelId[] },
        { dispatch, rejectWithValue },
      ) => {
        try {
          await closeManyCurrentRentParcelApi(parcelIds, closingDate);
        } catch (e) {
          return rentError(e, dispatch, rejectWithValue);
        }
        success(
          isDesarchive
            ? 'ParcelHistory.active_many_parcel_success'
            : 'ParcelHistory.close_many_current_parcel_rent_success',
          { dispatch, duration: 5000 },
        );
        await Promise.all(parcelIds.map((id) => refreshParcelAndHistory(dispatch, id)));
        return;
      },
    ),
    createParcel: create.asyncThunk(
      async (payload: ParcelCreationPayload | ParcelCreationPayload[], { dispatch }) => {
        let result;
        try {
          result = (await createParcelApi(payload)) as {
            errors?: ParcelCreationError[];
            success?: Parcel[];
            id?: ParcelId;
          };
        } catch (e) {
          parcelError(e as ParcelApiError, 'ParcelForm.parcel_create_error', dispatch);
          throw e;
        }
        const { errors, success: created, id } = result;
        const createdEntities = created?.length
          ? (normalize(created, [parcelSchema]) as NormalizedParcels)
          : id
            ? (normalize(result, parcelSchema) as NormalizedParcels)
            : null;
        if (createdEntities) {
          success('ParcelForm.parcel_create_success', { dispatch, duration: 5000 });
        }
        if (errors?.length) {
          console.warn('failed to add some fields', errors);
          // the throw below skips the fulfilled reducer, so a partial batch is merged here
          if (createdEntities) {
            dispatch(parcelsSlice.actions.parcelsCreated(createdEntities));
          }
          dispatch(parcelsSlice.actions.parcelCreationErrorsReceived(errors));
          parcelError(errors[0]!, 'ParcelForm.parcel_create_error', dispatch);
          throw errors;
        }
        return { created: createdEntities, errors: errors ?? [], success: created };
      },
      {
        fulfilled: (state, { payload }) => {
          if (payload.created) {
            mergeParcels(state, payload.created);
          }
        },
      },
    ),
    mergeExistingParcels: create.asyncThunk(
      async (
        {
          parcelIds,
          parcel,
        }: { parcelIds: ParcelId[]; parcel: ParcelCreationPayload & { name: string; from_date: string } },
        { dispatch },
      ) => {
        try {
          // archive the existing parcels first
          await alerting(() => createMultiParcelRentApi(parcelIds, { from_date: parcel.from_date }), { dispatch });
          observationsArchived({ parcelIds }, { dispatch });

          // create the merged parcel
          const { errors } = (await createParcelApi([parcel]).catch((e: ParcelApiError) => {
            parcelError(e, 'ParcelForm.parcel_create_error', dispatch);
            throw e;
          })) as { errors?: ParcelCreationError[] };

          const error = errors?.at(0);

          if (error) {
            parcelError(error, 'ParcelForm.parcel_create_error', dispatch);
            throw errors;
          }

          success('MergeParcelsModal.merge_success', {
            dispatch,
            duration: 5000,
            params: { name: parcel.name, smart_count: parcelIds.length, date: moment(parcel.from_date).format('L') },
          });
        } finally {
          await dispatch(parcelsSlice.actions.fetchParcels());
        }
      },
    ),
    linkTagToParcel: create.asyncThunk(
      async ({ parcelId, tag }: { parcelId: ParcelId; tag: Tag }, { dispatch }) => {
        await alerting(() => linkTagToParcelApi(parcelId, tag.id), { dispatch });
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          addTag(state, arg.parcelId, arg.tag);
        },
      },
    ),
    unlinkTagFromParcel: create.asyncThunk(
      async ({ parcelId, tagId }: { parcelId: ParcelId; tagId: TagId }, { dispatch }) => {
        await alerting(() => unlinkTagFromParcelApi(parcelId, tagId), { dispatch });
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          removeTag(state, arg.parcelId, arg.tagId);
        },
      },
    ),
    linkManyTagsToParcels: create.asyncThunk(
      async (parcelsAndTags: { parcel_id: ParcelId; tag: Tag }[], { dispatch }) => {
        await alerting(
          () => linkManyTagsToParcelsApi(parcelsAndTags.map(({ parcel_id, tag }) => ({ parcel_id, tag_id: tag.id }))),
          { dispatch },
        );
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          for (const { parcel_id, tag } of arg) {
            addTag(state, parcel_id, tag);
          }
        },
      },
    ),
    unlinkManyTagsFromParcels: create.asyncThunk(
      async (parcelsAndTagIds: { parcel_id: ParcelId; tag_id: TagId }[], { dispatch }) => {
        await alerting(() => unlinkManyTagsFromParcelsApi(parcelsAndTagIds), { dispatch });
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          for (const { parcel_id, tag_id } of arg) {
            removeTag(state, parcel_id, tag_id);
          }
        },
      },
    ),
  }),
  extraReducers: (builder) => {
    builder.addCase(deleteCrop.fulfilled, (state, { payload }) => {
      for (const parcel of Object.values(state.parcelById)) {
        if (parcel.current_crop_id === payload) {
          parcel.current_crop_id = undefined;
        }
      }
    });

    builder.addCase(userLoggedOut, () => initialState);
  },
  selectors: {
    selectIsParcelsFetching: (state) => state.isFetching,
    selectAreImportedParcelsFetching: (state) => state.areImportedParcelsFetching,
    selectParcelsById: (state) => state.parcelById,
    selectParcelsIds: (state) => state.parcelsIds,
    selectImportedParcels: (state) => state.importedParcels,
    selectClusterParcelsByParcelId: (state) => state.clusterParcelsByParcelId,
    selectLastFetchedParcel: (state) => state.lastFetchedParcel,
    selectFocusedParcel: (state) => state.focusedParcel,
    selectParcelCreationErrors: (state) => state.creationErrors,
    selectParcel: (state, id: ParcelId) => state.parcelById[id],
  },
});

export const {
  selectIsParcelsFetching,
  selectAreImportedParcelsFetching,
  selectParcelCreationErrors,
  selectFocusedParcel,
  selectParcelsById,
  selectParcelsIds,
  selectImportedParcels,
  selectClusterParcelsByParcelId,
  selectLastFetchedParcel,
  selectParcel,
} = parcelsSlice.selectors;

export const {
  fetchParcel,
  fetchParcels,
  fetchAnonymousParcelsByIds,
  focusParcel,
  clearParcelCreationErrors,
  editParcel,
  patchParcel,
  deleteParcel,
  deleteMultiParcels,
  importParcels,
  reinitImportedParcels,
  createGfrParcels,
  fetchParcelHistory,
  createMultiParcelRent,
  createParcelRent,
  updateClusterParcel,
  deleteClusterParcel,
  closeCurrentRentParcel,
  closeManyCurrentRentParcel,
  createParcel,
  mergeExistingParcels,
  linkTagToParcel,
  unlinkTagFromParcel,
  linkManyTagsToParcels,
  unlinkManyTagsFromParcels,
} = parcelsSlice.actions;

export default parcelsSlice.reducer;
